import { Application, Container, FederatedPointerEvent } from 'pixi.js'
import type { GridCoord, ImportedCity, Plan, ResolvedBuilding } from '$lib/types'
import { resolveBuilding } from '$lib/catalogue'
import type { ActiveTool } from '$lib/editor'
import { colours } from './colours'
import { GridLayer } from './layers/grid-layer'
import { BuildingLayer } from './layers/building-layer'
import { OverlayLayer } from './layers/overlay-layer'
import { buildingAtGrid, type RenderableBuilding } from './hit-test'

const DEFAULT_CELL_SIZE = 24
const MIN_ZOOM = 0.2
const MAX_ZOOM = 4
const ZOOM_STEP = 1.15
/**
 * Pixel threshold before a left-click on a building turns into a drag rather
 * than a select. Roughly the size of one cell at default zoom; tight enough
 * that intentional drags register but loose enough that a slightly shaky
 * click still selects.
 */
const DRAG_THRESHOLD_PX = 3

export interface SceneCallbacks {
  onSelect?: (id: number | null) => void
  onHover?: (id: number | null) => void
  /** Drag-to-move on the grid. `from` and `to` are top-left grid cells. */
  onMove?: (id: number, from: GridCoord, to: GridCoord) => void
  /**
   * Pointer up while a placement is active. The editor is responsible for
   * looking up the placement spec it set via `beginPlacement` and dispatching
   * the right command (place from inventory vs from palette).
   */
  onPlacementDrop?: (at: GridCoord) => void
  /** Esc / pointerup-elsewhere while placing. */
  onPlacementCancel?: () => void
  /** Fired whenever the world zoom or pan changes (wheel zoom, pan drag,
   *  fitToScreen, centerOn, applyViewState). The editor uses this to
   *  persist `lastViewState` so reloading the page restores the camera.
   *  Fires often during interaction — callers should debounce. */
  onViewChange?: (state: ViewState) => void
}

export interface ViewState {
  zoom: number
  panX: number
  panY: number
}

/**
 * Tool-agnostic pointer event passed to external tool handlers (road,
 * store). The scene pre-resolves the grid coord and the building under
 * the cursor so handlers don't need to import any Pixi types.
 */
export interface ToolPointerEvent {
  /** Grid cell under the cursor. */
  grid: GridCoord
  /** Building under the cursor, or null if empty. */
  target: RenderableBuilding | null
  /** 0 = primary (left), 1 = auxiliary (middle), 2 = secondary (right). */
  button: number
}

export interface ToolPointerHandlers {
  onPointerDown?(e: ToolPointerEvent): void
  onPointerMove?(e: ToolPointerEvent): void
  onPointerUp?(e: ToolPointerEvent): void
  /** Called by Esc / cancelInFlightGesture. Should drop any in-flight
   *  gesture state and clear visual previews. Returns whether something
   *  was cancelled, so the caller can stop the cancel-cascade. */
  onCancel?(): boolean
  /** Called when the tool switches away. Default semantics: same as
   *  onCancel — drop in-flight state, clear previews. */
  onDeactivate?(): void
}

export interface SceneInputs {
  plan: Plan
  importedCity: ImportedCity
  selectedId: number | null
  /** Optional: building ids the validator flagged. Drawn as red overlays. */
  invalidIds?: ReadonlySet<number>
}

export interface PlacementSpec {
  width: number
  height: number
}

interface DragMoveState {
  id: number
  /** Cursor offset within the building footprint, in grid cells. */
  offset: { dx: number; dy: number }
  /** Original top-left grid position. */
  from: GridCoord
  /** Current top-left grid position the ghost is snapped to. */
  to: GridCoord
  /** Footprint dimensions, used for ghost rendering. */
  width: number
  height: number
  /** Pixel-space pointerdown origin so we can apply the drag threshold. */
  startScreenX: number
  startScreenY: number
  /** True once the cursor has moved past the threshold. */
  active: boolean
}

interface PanState {
  startX: number
  startY: number
  worldStartX: number
  worldStartY: number
  didPan: boolean
}

export class CityScene {
  readonly app: Application
  private host: HTMLElement
  /** World-space container; pan/zoom transforms are applied here. */
  private readonly world: Container = new Container()
  private readonly grid: GridLayer
  private readonly buildings: BuildingLayer
  private readonly overlay: OverlayLayer
  private cellSize = DEFAULT_CELL_SIZE
  private renderables: RenderableBuilding[] = []
  private invalidIds: ReadonlySet<number> = new Set()
  private selectedId: number | null = null
  private hoverId: number | null = null
  private callbacks: SceneCallbacks = {}
  private resizeObs?: ResizeObserver
  private destroyed = false
  /** Last `update()` inputs, kept so `applyTheme()` can replay them. */
  private lastInputs: SceneInputs | null = null

  // Pointer state — at most one of these is non-null at a time. The pan
  // and drag states are owned by the select tool; placement is shared
  // across tools (any tool's pointer flow is paused while a pickup is
  // active and the next click drops).
  private pan: PanState | null = null
  private drag: DragMoveState | null = null
  private placement: (PlacementSpec & { current: GridCoord | null }) | null = null

  /** Active tool. Defaults to 'select' so a freshly-created scene matches
   *  Phase 2 behaviour without any editor wiring. */
  private activeTool: ActiveTool = 'select'

  /** Pointer handlers registered by the editor for non-select tools. The
   *  scene routes pointer events through these so road / store logic can
   *  live in feature files without dragging Pixi types across the seam. */
  private externalToolHandlers: Partial<
    Record<Exclude<ActiveTool, 'select'>, ToolPointerHandlers>
  > = {}

  private constructor(host: HTMLElement, app: Application) {
    this.host = host
    this.app = app
    this.app.stage.addChild(this.world)
    this.grid = new GridLayer(this.cellSize)
    this.buildings = new BuildingLayer(this.cellSize)
    this.overlay = new OverlayLayer(this.cellSize)
    this.world.addChild(this.grid.container)
    this.world.addChild(this.buildings.container)
    this.world.addChild(this.overlay.container)
  }

  static async create(host: HTMLElement, callbacks: SceneCallbacks = {}): Promise<CityScene> {
    const app = new Application()
    await app.init({
      resizeTo: host,
      backgroundColor: colours.bgBase(),
      antialias: true,
      autoDensity: true,
      resolution: window.devicePixelRatio || 1,
    })
    host.appendChild(app.canvas)
    const scene = new CityScene(host, app)
    scene.callbacks = callbacks
    scene.bindEvents()
    return scene
  }

  setCallbacks(cb: SceneCallbacks): void {
    this.callbacks = cb
  }

  /** Update inputs and redraw. Cheap to call on every store change. */
  update(inputs: SceneInputs): void {
    const { plan, importedCity, selectedId, invalidIds } = inputs
    this.lastInputs = inputs
    this.selectedId = selectedId
    this.invalidIds = invalidIds ?? new Set()
    this.lastUnlockedAreas = plan.unlockedAreas
    this.renderables = plan.buildings.map((building) => {
      const resolved = resolveBuilding(building.cityentityId, importedCity)
      return {
        building,
        resolved,
        // Unknowns and zero-sized entities default to 1×1 so they show up.
        width: resolved && resolved.width > 0 ? resolved.width : 1,
        height: resolved && resolved.height > 0 ? resolved.height : 1,
      }
    })

    this.grid.draw(plan.unlockedAreas)
    this.buildings.draw(this.renderables, this.world.scale.x)
    this.overlay.drawInvalid(this.renderables, this.invalidIds)
    this.overlay.drawSelection(this.findById(this.selectedId))
    this.overlay.drawHover(this.findById(this.hoverId))
    // The drag/placement ghost lives outside `update()`: it's repainted on
    // pointermove, not on plan change. Don't clobber it here.
    if (this.drag?.active) this.repaintDragGhost()
    else if (this.placement?.current) this.repaintPlacementGhost()
  }

  /**
   * Switch the active tool. Cancels any in-flight pan or building drag so
   * a tool change mid-gesture doesn't leave dangling state. Placement (the
   * inventory/palette pickup state) survives a tool switch by design — the
   * pickup is independent of which tool's pointer machine is otherwise
   * active.
   */
  setActiveTool(tool: ActiveTool): void {
    if (this.activeTool === tool) return
    this.pan = null
    this.drag = null
    if (!this.placement) this.overlay.clearGhost()
    // Notify the outgoing tool so it can drop any in-flight gesture (e.g.
    // a road paint stroke that was mid-flight when the user pressed S).
    if (this.activeTool !== 'select') {
      this.externalToolHandlers[this.activeTool]?.onDeactivate?.()
    }
    this.activeTool = tool
  }

  /**
   * Register external pointer handlers for non-select tools. Editors call
   * this once during scene setup; the scene routes pointer events through
   * the matching entry when activeTool is 'road' or 'store'. Keeping the
   * tool-specific logic out of the scene means the road / store handler
   * modules can live in features/ without dragging Pixi types across the
   * lib/features seam.
   */
  setExternalToolHandlers(
    handlers: Partial<Record<Exclude<ActiveTool, 'select'>, ToolPointerHandlers>>,
  ): void {
    this.externalToolHandlers = handlers
  }

  /**
   * Drop any in-flight non-placement pointer gesture (pan, building drag)
   * and return whether something was cancelled. The editor's Esc handler
   * uses this so a mid-drag Esc cancels the gesture without affecting the
   * active tool. Placement (pickup) cancellation runs through a separate
   * editor path because the editor owns the drop intent.
   */
  cancelInFlightGesture(): boolean {
    let cancelled = false
    if (this.pan) {
      this.pan = null
      cancelled = true
    }
    if (this.drag) {
      this.drag = null
      this.overlay.clearGhost()
      cancelled = true
    }
    // External tool gesture (e.g. an active road paint stroke).
    if (this.activeTool !== 'select') {
      const handlers = this.externalToolHandlers[this.activeTool]
      if (handlers?.onCancel?.() === true) cancelled = true
    }
    return cancelled
  }

  /** Stroke preview pass-through for tools that paint multi-square overlays
   *  (the road tool today). The scene owns the overlay layer; tools call
   *  these via the context object the editor builds for them. */
  drawStrokePreview(squares: ReadonlyArray<GridCoord>): void {
    this.overlay.drawStrokePreview(squares)
  }

  clearStrokePreview(): void {
    this.overlay.clearStrokePreview()
  }

  /** Mark placed buildings as "caught in a store-tool sweep". The overlay
   *  draws a red tint over each id — the player sees what's about to be
   *  sent to inventory before they release the mouse. Replaces the
   *  previous mark set on every call. */
  markBuildingsForStore(ids: ReadonlySet<number>): void {
    this.overlay.drawStorePreview(ids, this.renderables)
  }

  clearStoreMarks(): void {
    this.overlay.clearStorePreview()
  }

  /** External entry-point for inventory/palette pickups. Editor calls this
   *  when the user picks an item; the scene draws a ghost following the
   *  cursor and emits `onPlacementDrop` on the next pointerup over the canvas. */
  beginPlacement(spec: PlacementSpec): void {
    this.placement = { width: spec.width, height: spec.height, current: null }
    // No ghost yet — wait for the first pointermove so the ghost appears
    // exactly where the cursor is, not at (0,0).
    this.overlay.clearGhost()
  }

  cancelPlacement(): void {
    if (!this.placement) return
    this.placement = null
    this.overlay.clearGhost()
  }

  /** Centre + zoom-to-fit the union of all unlocked areas. */
  fitToScreen(): void {
    if (this.renderables.length === 0 && this.grid.container.children.length === 0) return
    const bounds = this.computeWorldBounds()
    if (!bounds) return
    const padding = 40
    const sx = (this.app.renderer.width - padding * 2) / bounds.width
    const sy = (this.app.renderer.height - padding * 2) / bounds.height
    const scale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.min(sx, sy)))
    this.world.scale.set(scale)
    this.world.position.set(
      this.app.renderer.width / 2 - (bounds.x + bounds.width / 2) * scale,
      this.app.renderer.height / 2 - (bounds.y + bounds.height / 2) * scale,
    )
    this.buildings.applyZoomTier(scale)
    this.notifyViewChange()
  }

  zoomTo(scale: number, anchorX?: number, anchorY?: number): void {
    const target = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, scale))
    const cx = anchorX ?? this.app.renderer.width / 2
    const cy = anchorY ?? this.app.renderer.height / 2
    const oldScale = this.world.scale.x
    const wx = (cx - this.world.position.x) / oldScale
    const wy = (cy - this.world.position.y) / oldScale
    this.world.scale.set(target)
    this.world.position.set(cx - wx * target, cy - wy * target)
    // Building labels' visibility tier depends on screen-pixel size, which
    // changes with the world scale. Swapping full / short / hidden in place
    // is much cheaper than a full layer redraw.
    this.buildings.applyZoomTier(target)
    this.notifyViewChange()
  }

  zoomIn(): void {
    this.zoomTo(this.world.scale.x * ZOOM_STEP)
  }

  zoomOut(): void {
    this.zoomTo(this.world.scale.x / ZOOM_STEP)
  }

  zoomReset(): void {
    this.zoomTo(1)
  }

  /** Pan the world so the given grid cell sits at the canvas centre. Zoom is
   *  preserved. Used by the validation banner's "show" button to bring the
   *  next offending building into view. */
  centerOn(gridX: number, gridY: number): void {
    const scale = this.world.scale.x
    const cx = this.app.renderer.width / 2
    const cy = this.app.renderer.height / 2
    const targetWorldX = (gridX + 0.5) * this.cellSize
    const targetWorldY = (gridY + 0.5) * this.cellSize
    this.world.position.set(cx - targetWorldX * scale, cy - targetWorldY * scale)
    this.notifyViewChange()
  }

  /** Snapshot the current view (zoom + pan, in world coordinates the editor
   *  doesn't need to interpret — applyViewState reverses them). */
  getViewState(): ViewState {
    return {
      zoom: this.world.scale.x,
      panX: this.world.position.x,
      panY: this.world.position.y,
    }
  }

  /** Restore a previously-captured view state. Returns true if the state
   *  was applied; false if the input fails the basic validity check (zoom
   *  out of range, non-finite numbers). The editor uses the return value
   *  to decide whether to fall back to fitToScreen on mount. */
  applyViewState(state: ViewState): boolean {
    if (
      !Number.isFinite(state.zoom) ||
      !Number.isFinite(state.panX) ||
      !Number.isFinite(state.panY)
    ) {
      return false
    }
    const z = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, state.zoom))
    this.world.scale.set(z)
    this.world.position.set(state.panX, state.panY)
    this.buildings.applyZoomTier(z)
    this.notifyViewChange()
    return true
  }

  private notifyViewChange(): void {
    this.callbacks.onViewChange?.(this.getViewState())
  }

  /**
   * Re-read theme variables and redraw. Layers don't cache colours, but
   * the building-layer caches truncated label text per building (binary-
   * search measurements aren't free). Strings stay valid across themes —
   * the cache is invalidated here only because the contrast-aware text
   * colour is recomputed on every draw and we want a fresh pass. Called
   * by the editor host whenever the theme cycles.
   */
  applyTheme(): void {
    this.app.renderer.background.color = colours.bgBase()
    this.buildings.invalidateCache()
    if (this.lastInputs) this.update(this.lastInputs)
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.resizeObs?.disconnect()
    this.app.destroy(true, { children: true, texture: true })
  }

  // — internals —

  private bindEvents(): void {
    const stage = this.app.stage
    stage.eventMode = 'static'
    stage.hitArea = this.app.screen

    stage.on('pointerdown', (e) => this.onPointerDown(e))
    stage.on('pointermove', (e) => this.onPointerMove(e))
    stage.on('pointerup', (e) => this.onPointerUp(e))
    stage.on('pointerupoutside', (e) => this.onPointerUp(e))

    this.app.canvas.addEventListener('wheel', this.onWheel, { passive: false })

    this.resizeObs = new ResizeObserver(() => {
      stage.hitArea = this.app.screen
    })
    this.resizeObs.observe(this.host)
  }

  private onWheel = (ev: WheelEvent): void => {
    ev.preventDefault()
    const factor = ev.deltaY > 0 ? 1 / ZOOM_STEP : ZOOM_STEP
    const rect = this.app.canvas.getBoundingClientRect()
    const ax = ev.clientX - rect.left
    const ay = ev.clientY - rect.top
    this.zoomTo(this.world.scale.x * factor, ax, ay)
  }

  private onPointerDown(e: FederatedPointerEvent): void {
    // Placement always wins, regardless of active tool: while picking from
    // inventory/palette, the next canvas click drops on whatever's under it.
    if (this.placement) return

    // Middle-click pans regardless of tool — gives every tool a consistent
    // pan affordance without needing tool-specific handling.
    if (e.button === 1) {
      this.startPan(e)
      return
    }

    if (this.activeTool === 'select') {
      this.selectHandlers.pointerDown(e)
      return
    }
    this.externalToolHandlers[this.activeTool]?.onPointerDown?.(this.toToolEvent(e))
  }

  private onPointerMove(e: FederatedPointerEvent): void {
    if (this.placement) {
      const grid = this.gridAt(e)
      this.placement.current = grid
      this.repaintPlacementGhost()
      return
    }

    // Pan can be in flight from middle-click in any tool, or from a
    // left-click on empty grid in select mode. Either way, pan move
    // belongs at the dispatch level.
    if (this.pan) {
      const dx = e.global.x - this.pan.startX
      const dy = e.global.y - this.pan.startY
      if (Math.abs(dx) + Math.abs(dy) > 2) this.pan.didPan = true
      this.world.position.set(this.pan.worldStartX + dx, this.pan.worldStartY + dy)
      // Pan fires per-pixel; the editor debounces persistence.
      this.notifyViewChange()
      return
    }

    if (this.activeTool === 'select') {
      this.selectHandlers.pointerMove(e)
      return
    }
    this.externalToolHandlers[this.activeTool]?.onPointerMove?.(this.toToolEvent(e))
  }

  private onPointerUp(e: FederatedPointerEvent): void {
    if (this.placement) {
      if (e.button !== 0) return
      const grid = this.gridAt(e)
      this.callbacks.onPlacementDrop?.(grid)
      // Editor will call cancelPlacement after dispatching.
      return
    }

    if (this.pan) {
      const wasPan = this.pan.didPan
      this.pan = null
      if (wasPan) return
    }

    if (this.activeTool === 'select') {
      this.selectHandlers.pointerUp(e)
      return
    }
    this.externalToolHandlers[this.activeTool]?.onPointerUp?.(this.toToolEvent(e))
  }

  private toToolEvent(e: FederatedPointerEvent): ToolPointerEvent {
    return {
      grid: this.gridAt(e),
      target: this.hitAt(e),
      button: e.button,
    }
  }

  /**
   * Select tool's pointer state machine. Pan is handled in the dispatcher
   * (above) since it's tool-agnostic — middle-click pans in any mode, and
   * a left-click on empty grid here starts a pan that the dispatcher then
   * follows on subsequent move/up. Drag-to-move and click-to-select stay
   * select-specific.
   */
  private readonly selectHandlers = {
    pointerDown: (e: FederatedPointerEvent): void => {
      // Dispatcher already handled middle-click. We only see button === 0
      // (and the rare button === 2 right-click which we ignore).
      if (e.button !== 0) return

      const target = this.hitAt(e)
      if (target) {
        this.startBuildingDrag(e, target)
      } else {
        this.startPan(e)
      }
    },

    pointerMove: (e: FederatedPointerEvent): void => {
      if (this.drag) {
        const dx = e.global.x - this.drag.startScreenX
        const dy = e.global.y - this.drag.startScreenY
        if (!this.drag.active && Math.abs(dx) + Math.abs(dy) > DRAG_THRESHOLD_PX) {
          this.drag.active = true
        }
        if (this.drag.active) {
          const cursor = this.gridAt(e)
          // Snap so the cursor's relative grip position inside the building stays
          // constant — clicking the centre of a 3×4 should keep the cursor on
          // that centre cell as the ghost moves.
          this.drag.to = {
            x: cursor.x - this.drag.offset.dx,
            y: cursor.y - this.drag.offset.dy,
          }
          this.repaintDragGhost()
        }
        return
      }

      // Plain hover.
      const target = this.hitAt(e)
      const id = target ? target.building.id : null
      if (id !== this.hoverId) {
        this.hoverId = id
        this.overlay.drawHover(target)
        this.callbacks.onHover?.(id)
      }
    },

    pointerUp: (e: FederatedPointerEvent): void => {
      if (this.drag) {
        const drag = this.drag
        this.drag = null
        this.overlay.clearGhost()
        if (drag.active && (drag.to.x !== drag.from.x || drag.to.y !== drag.from.y)) {
          this.callbacks.onMove?.(drag.id, drag.from, drag.to)
        } else {
          // No movement → treat as a select.
          if (drag.id !== this.selectedId) {
            this.selectedId = drag.id
            this.overlay.drawSelection(this.findById(drag.id))
            this.callbacks.onSelect?.(drag.id)
          }
        }
        return
      }

      if (e.button !== 0) return
      const target = this.hitAt(e)
      const id = target ? target.building.id : null
      if (id !== this.selectedId) {
        this.selectedId = id
        this.overlay.drawSelection(target)
        this.callbacks.onSelect?.(id)
      }
    },
  }

  private startPan(e: FederatedPointerEvent): void {
    this.pan = {
      startX: e.global.x,
      startY: e.global.y,
      worldStartX: this.world.position.x,
      worldStartY: this.world.position.y,
      didPan: false,
    }
  }

  private startBuildingDrag(e: FederatedPointerEvent, target: RenderableBuilding): void {
    const cursor = this.gridAt(e)
    this.drag = {
      id: target.building.id,
      offset: {
        dx: cursor.x - target.building.x,
        dy: cursor.y - target.building.y,
      },
      from: { x: target.building.x, y: target.building.y },
      to: { x: target.building.x, y: target.building.y },
      width: target.width,
      height: target.height,
      startScreenX: e.global.x,
      startScreenY: e.global.y,
      active: false,
    }
  }

  private repaintDragGhost(): void {
    if (!this.drag) return
    this.overlay.drawGhost(this.drag.to.x, this.drag.to.y, this.drag.width, this.drag.height, true)
  }

  private repaintPlacementGhost(): void {
    if (!this.placement || !this.placement.current) return
    this.overlay.drawGhost(
      this.placement.current.x,
      this.placement.current.y,
      this.placement.width,
      this.placement.height,
      true,
    )
  }

  private hitAt(e: FederatedPointerEvent): RenderableBuilding | null {
    const g = this.gridAt(e)
    return buildingAtGrid(this.renderables, g.x, g.y)
  }

  private gridAt(e: FederatedPointerEvent): GridCoord {
    const local = this.world.toLocal(e.global)
    return {
      x: Math.floor(local.x / this.cellSize),
      y: Math.floor(local.y / this.cellSize),
    }
  }

  private findById(id: number | null): RenderableBuilding | null {
    if (id === null) return null
    return this.renderables.find((r) => r.building.id === id) ?? null
  }

  private computeWorldBounds(): { x: number; y: number; width: number; height: number } | null {
    const c = this.cellSize
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const tile of this.lastUnlockedAreas) {
      minX = Math.min(minX, tile.x * c)
      minY = Math.min(minY, tile.y * c)
      maxX = Math.max(maxX, (tile.x + tile.width) * c)
      maxY = Math.max(maxY, (tile.y + tile.length) * c)
    }
    if (!Number.isFinite(minX)) return null
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
  }

  private lastUnlockedAreas: { x: number; y: number; width: number; length: number }[] = []

  /** Exposed so the detail panel can pull the resolved record. */
  static resolved(b: RenderableBuilding): ResolvedBuilding | null {
    return b.resolved
  }
}
